import React, { useState } from "react";
import { X, Mail, Lock, User as UserIcon } from "lucide-react";
import { C, SERIF, SANS } from "./constants";
import { useAuth } from "./AuthContext";
import { Btn } from "../components/Primitives";

export function AuthModal() {
  const { authModalOpen, closeAuth, signup, login } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (!authModalOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "signup") await signup(email, password, name || undefined);
      else await login(email, password);
      closeAuth();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const input = {
    width: "100%", padding: "12px 14px 12px 40px", border: `1.5px solid ${C.border}`,
    borderRadius: 8, fontFamily: SANS, fontSize: 14, color: C.dark,
    background: C.bg, outline: "none", boxSizing: "border-box",
  };
  const label = { fontFamily: SANS, fontSize: 12, fontWeight: 600, color: C.dark, marginBottom: 6, display: "block" };

  return (
    <>
      <div onClick={closeAuth} style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,0.4)", zIndex: 399 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 400,
        background: C.white, borderRadius: 18, padding: "30px 32px", width: 430, maxWidth: "92vw",
        boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 26, color: C.dark, fontWeight: 400, margin: 0 }}>
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </h2>
          <button onClick={closeAuth} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ fontFamily: SANS, fontSize: 13, color: C.muted, margin: "0 0 22px", lineHeight: 1.6 }}>
          {mode === "signup"
            ? "Your cart, tracker, and history — kept safely across devices."
            : "Sign in to sync your cart, tracker, and community."}
        </p>

        <button onClick={googleLogin} style={{
          width: "100%", padding: "12px 16px", border: `1.5px solid ${C.border}`,
          borderRadius: 8, background: C.white, cursor: "pointer", fontFamily: SANS,
          fontSize: 13, fontWeight: 600, color: C.dark, display: "flex",
          alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16,
        }}>
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Continue with Google
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "6px 0 18px" }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt, letterSpacing: 1 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        <form onSubmit={submit}>
          {mode === "signup" && (
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Name (optional)</label>
              <div style={{ position: "relative" }}>
                <UserIcon size={15} color={C.mutedLt} style={{ position: "absolute", left: 14, top: 14 }} />
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={input} />
              </div>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={label}>Email</label>
            <div style={{ position: "relative" }}>
              <Mail size={15} color={C.mutedLt} style={{ position: "absolute", left: 14, top: 14 }} />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com" required style={input} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={label}>Password</label>
            <div style={{ position: "relative" }}>
              <Lock size={15} color={C.mutedLt} style={{ position: "absolute", left: 14, top: 14 }} />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "Choose a password" : "Your password"}
                required minLength={6} style={input} />
            </div>
          </div>
          {err && (
            <div style={{
              background: "#FDECE7", color: "#8A3E22", fontFamily: SANS, fontSize: 12,
              padding: "10px 12px", borderRadius: 8, marginBottom: 12,
            }}>{err}</div>
          )}
          <Btn full size="lg" onClick={submit}>
            {loading ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
          </Btn>
        </form>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: SANS, fontSize: 13, color: C.muted }}>
            {mode === "signup" ? "Already have an account? " : "New here? "}
            <span style={{ color: C.coral, fontWeight: 600, textDecoration: "underline" }}>
              {mode === "signup" ? "Sign in" : "Create one"}
            </span>
          </button>
        </div>
      </div>
    </>
  );
}

export function AuthCallback({ onDone }) {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("Signing you in…");
  React.useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const { authApi } = await import("./api");
        await authApi.emergentSession(session_id);
        window.history.replaceState(null, "", window.location.pathname);
        await refreshUser();
        setStatus("Signed in — welcome home.");
        onDone && onDone();
      } catch (e) {
        setStatus("Sign-in failed. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: SERIF, fontSize: 22, color: C.muted }}>
      {status}
    </div>
  );
}
